import React, { useState } from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { Box, IconButton, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import RoomIcon from "@mui/icons-material/Room";
import CloseIcon from "@mui/icons-material/Close";
import CircleIcon from "@mui/icons-material/Circle";

import useUserPreferences from "components/hooks/useUserPreferences";

import { getLocationTypeColor } from "view/locations/utils/LocationUtils";
import { CallStatusColor } from "view/calls/utils/CallUtils";

import { DataFormat, hasPluginPermission } from "imports/Utils";

import { CallTimeSource, MapTabs } from "../../utils/MapUtils";

import { LocationType } from "__generated__/enums";

const MapLegend = ({ type, callTimeSource }: { type: MapTabs; callTimeSource: CallTimeSource }) => {

	const session = useStoreState((state) => state.session);

	const statuses = session.statuses?.CALL ?? [];

	const [ collapsed, setCollapsed ] = useState<boolean>(session.user?.properties.userPreferences?.mapSettings?.legendCollapsed ?? false);

	const saveUserPreferences = useUserPreferences();

	const onToggle = (collapse: boolean) => {
		setCollapsed(collapse);

		const currentPreferences = session.user?.properties.userPreferences?.mapSettings || {};

		saveUserPreferences({
			preferences: {
				mapSettings: {
					...currentPreferences,
					legendCollapsed: collapse,
				},
			},
		});
	};

	const baseControlStyles = {
		backgroundColor: "background.paper",
		boxShadow: "rgba(0, 0, 0, 0.3) 0px 1px 4px -1px",
		borderRadius: 1,
		bottom: 24,
		left: 24,
		position: "absolute",
		zIndex: 2,
	};

	const baseTextStyles = {
		color: "text.primary",
		fontWeight: 500,
		fontSize: 12,
	};

	if (collapsed) {
		return (
			<Box
				sx={{
					...baseControlStyles,
					alignItems: "center",
					display: "flex",
					cursor: "pointer",
					height: 40,
					justifyContent: "center",
					width: 40,
				}}
				onClick={() => onToggle(false)}>
				<FormatListBulletedIcon color="action" />
			</Box>
		);
	}

	return (
		<Box sx={{ ...baseControlStyles, width: 200 }}>
			<Box
				sx={{
					alignItems: "center",
					display: "flex",
					justifyContent: "space-between",
					pl: 1.5,
					pt: 1.5,
					pr: 1.5,
				}}>
				<Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1, color: "text.primary" }}>
					{intl.formatMessage({ id: "MAP_LEGEND" })}
				</Typography>

				<IconButton size="small" onClick={() => onToggle(true)}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			{type === MapTabs.Locations && (
				<List dense>
					{Object.values(LocationType).map((locType) => {
						return (
							<ListItem key={locType} sx={{ px: 2 }}>
								<ListItemIcon sx={{ minWidth: 32 }}>
									<RoomIcon fontSize="small" htmlColor={getLocationTypeColor(locType)} />
								</ListItemIcon>

								<ListItemText
									sx={{ m: 0 }}
									primary={(
										<Typography variant="subtitle2" sx={baseTextStyles}>
											{intl.formatMessage({ id: locType })}
										</Typography>
									)} />
							</ListItem>
						);
					})}
				</List>
			)}

			{type === MapTabs.Ships && (
				<List dense>
					{[ {
						color: "#45B741",
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_ACTUAL" }),
					}, {
						color: "#9B9B9B",
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_NOT_ACTUAL" }),
					} ].map(it => (
						<ListItem sx={{ px: 2 }}>
							<ListItemIcon sx={{ minWidth: 32 }}>
								<CircleIcon fontSize="small" htmlColor={it.color} />
							</ListItemIcon>

							<ListItemText
								sx={{ m: 0 }}
								primary={(
									<Typography variant="subtitle2" sx={baseTextStyles}>
										{it.text}
									</Typography>
								)} />
						</ListItem>
					))}

					{!hasPluginPermission(session, "useContainers") && callTimeSource === CallTimeSource.Calculated ? [ {
						color: "#333",
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_BLACK" }),
					}, {
						color: CallStatusColor.GRAY,
						text: intl.formatMessage({ id: "UNKNOWN" }),
					}, {
						color: CallStatusColor.BLUE,
						text: intl.formatMessage({ id: "STATUSES_FINISHED_STATUS" }),
					}, {
						color: CallStatusColor.RED,
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_RED" }),
					}, {
						color: CallStatusColor.ORANGE,
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_ORANGE" }),
					}, {
						color: CallStatusColor.GREEN,
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_GREEN" }),
					} ].map(it => (
						<ListItem sx={{ px: 2 }} key={it.text}>
							<ListItemIcon sx={{ minWidth: 32 }}>
								<Box
									sx={{
										borderRadius: "50% 50% 2px 2px",
										height: 32,
										width: 18,
										background: it.color,
									}} />
							</ListItemIcon>

							<ListItemText
								sx={{ m: 0 }}
								primary={(
									<Typography variant="subtitle2" sx={baseTextStyles}>
										{it.text}
									</Typography>
								)} />
						</ListItem>
					)) : undefined}

					{hasPluginPermission(session, "useContainers") && callTimeSource === CallTimeSource.Calculated ? [ {
						color: CallStatusColor.GRAY,
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_EXECUTED" }),
					}, {
						color: CallStatusColor.RED,
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_RED" }),
					}, {
						color: CallStatusColor.ORANGE,
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_ORANGE" }),
					}, {
						color: CallStatusColor.GREEN,
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_GREEN" }),
					}, {
						color: CallStatusColor.PURPLE,
						text: intl.formatMessage({ id: "MAP_LEGEND_MARKER_IN_PROGRESS" }),
					}, {
						color: CallStatusColor.BLUE,
						text: intl.formatMessage({ id: "UNKNOWN" }),
					} ].map(it => (
						<ListItem sx={{ px: 2 }} key={it.text}>
							<ListItemIcon sx={{ minWidth: 32 }}>
								<Box
									sx={{
										borderRadius: "50% 50% 2px 2px",
										height: 32,
										width: 18,
										background: it.color,
									}} />
							</ListItemIcon>

							<ListItemText
								sx={{ m: 0 }}
								primary={(
									<Typography variant="subtitle2" sx={baseTextStyles}>
										{it.text}
									</Typography>
								)} />
						</ListItem>
					)) : undefined}

					{callTimeSource === CallTimeSource.Status && (
						<React.Fragment>
							{statuses.map((status) => (
								<ListItem sx={{ px: 2 }} key={status.id}>
									<ListItemIcon sx={{ minWidth: 32 }}>
										<Box
											sx={{
												borderRadius: "50% 50% 2px 2px",
												height: 32,
												width: 18,
												background: status.color,
											}} />
									</ListItemIcon>

									<ListItemText
										sx={{ m: 0 }}
										primary={(
											<Typography variant="subtitle2" sx={baseTextStyles}>
												{DataFormat.status(status, session.locale)}
											</Typography>
										)} />
								</ListItem>
							))}
						</React.Fragment>
					)}
				</List>
			)}
		</Box>
	);
};

export default MapLegend;
